/**
 * La file d'attente du terrain.
 *
 * Sans réseau, un geste (photo, démarrer, valider, réserve) n'est pas perdu : il
 * attend dans le téléphone (IndexedDB, qui garde les photos même si l'appli est
 * fermée) et repart tout seul au retour du réseau, dans l'ordre où il a été fait.
 *
 * Deux sortes d'échec, traitées différemment :
 * - le réseau manque : on garde le geste et on réessaiera ;
 * - le serveur refuse (« Il manque : 1 photo ») : réessayer ne servira à rien,
 *   on garde le geste marqué « refusé » avec la raison, pour que la personne la lise.
 */
import type {
  DepotChantiers,
  DepotSav,
  Heures,
  TypePreuve,
  TypePreuveSav,
} from '@/shared/donnees/types';

export type Geste =
  | {
      type: 'preuve';
      etapeId: string;
      typePreuve: TypePreuve;
      nom: string;
      mime: string;
      contenu: Blob;
    }
  /** `fin` : quand le partenaire pense finir (absent des gestes d'avant l'agenda). */
  | {
      type: 'demarrer';
      etapeId: string;
      fin?: string | null;
      debut?: string | null;
      heures?: Heures | null;
    }
  /** Annoncer une étape à venir, ou repousser la fin d'une étape en cours. */
  | { type: 'annoncer'; etapeId: string; debut: string | null; fin: string; heures?: Heures | null }
  | { type: 'soumettre'; etapeId: string }
  /** Avant S10, le partenaire validait lui-même : un tel geste resté dans le téléphone part comme un envoi au bureau. */
  | { type: 'valider'; etapeId: string }
  | { type: 'reserve'; etapeId: string; texte: string }
  /* Le SAV (S8) : les mêmes gestes, sur un ticket. */
  | {
      type: 'preuve-sav';
      ticketId: string;
      typePreuve: TypePreuveSav;
      nom: string;
      mime: string;
      contenu: Blob;
    }
  | { type: 'demarrer-sav'; ticketId: string }
  | { type: 'resoudre-sav'; ticketId: string; rapport: string };

/** L'étape ou le ticket que vise un geste. */
export function cibleDe(geste: Geste): string {
  return 'etapeId' in geste ? geste.etapeId : geste.ticketId;
}

export interface Operation {
  id: string;
  creeLe: string;
  geste: Geste;
  /** Présent quand le serveur a refusé : le geste ne repartira pas seul. */
  refus?: string;
}

export interface Stockage {
  lister(): Promise<Operation[]>;
  ecrire(operation: Operation): Promise<void>;
  retirer(id: string): Promise<void>;
}

export type Issue = 'fait' | 'en-attente';

/** Un échec dû au réseau, et non à une règle. */
export function estErreurReseau(erreur: unknown, enLigne: boolean): boolean {
  if (!enLigne) return true;
  if (erreur instanceof TypeError) return true;
  const message = erreur instanceof Error ? erreur.message.toLowerCase() : '';
  return /réseau|connexion au serveur|failed to fetch|network|load failed/.test(message);
}

export async function executer(
  geste: Geste,
  chantiers: DepotChantiers,
  sav: DepotSav | null = null
): Promise<void> {
  const ticket = (): DepotSav => {
    if (!sav) throw new Error('SAV indisponible.');
    return sav;
  };
  switch (geste.type) {
    case 'preuve':
      await chantiers.ajouterPreuve(
        geste.etapeId,
        new File([geste.contenu], geste.nom, { type: geste.mime }),
        geste.typePreuve
      );
      return;
    case 'demarrer':
      await chantiers.demarrer(
        geste.etapeId,
        geste.fin ?? null,
        geste.debut ?? null,
        geste.heures ?? null
      );
      return;
    case 'annoncer':
      await chantiers.annoncer(geste.etapeId, geste.debut, geste.fin, geste.heures ?? null);
      return;
    case 'soumettre':
    case 'valider':
      await chantiers.soumettre(geste.etapeId);
      return;
    case 'reserve':
      await chantiers.signalerReserve(geste.etapeId, geste.texte);
      return;
    case 'preuve-sav':
      await ticket().ajouterPreuve(
        geste.ticketId,
        new File([geste.contenu], geste.nom, { type: geste.mime }),
        geste.typePreuve
      );
      return;
    case 'demarrer-sav':
      await ticket().demarrer(geste.ticketId);
      return;
    case 'resoudre-sav':
      await ticket().resoudre(geste.ticketId, geste.rapport);
      return;
  }
}

export class FileAttente {
  private abonnes = new Set<() => void>();
  private enCours: Promise<void> | null = null;

  constructor(
    private readonly stockage: Stockage,
    private readonly chantiers: () => DepotChantiers,
    private readonly enLigne: () => boolean = () =>
      typeof navigator === 'undefined' ? true : navigator.onLine,
    private readonly sav: () => DepotSav | null = () => null
  ) {}

  sAbonner(rappel: () => void): () => void {
    this.abonnes.add(rappel);
    return () => this.abonnes.delete(rappel);
  }

  private prevenir(): void {
    this.abonnes.forEach((rappel) => {
      rappel();
    });
  }

  lister(): Promise<Operation[]> {
    return this.stockage
      .lister()
      .then((ops) => ops.sort((a, b) => a.creeLe.localeCompare(b.creeLe)));
  }

  /**
   * Faire le geste tout de suite si possible, sinon le garder. Un refus du
   * serveur remonte comme une erreur : la personne est là pour le lire.
   * Tant que des gestes attendent, le nouveau passe derrière eux : on ne valide
   * pas une étape avant que ses photos soient arrivées.
   */
  async soumettre(geste: Geste): Promise<Issue> {
    const enAttente = (await this.stockage.lister()).some((o) => o.refus === undefined);
    if (!enAttente && this.enLigne()) {
      try {
        await executer(geste, this.chantiers(), this.sav());
        return 'fait';
      } catch (erreur) {
        if (!estErreurReseau(erreur, this.enLigne())) throw erreur;
      }
    }
    await this.stockage.ecrire({
      id: crypto.randomUUID(),
      creeLe: new Date().toISOString(),
      geste,
    });
    this.prevenir();
    if (this.enLigne()) void this.vider();
    return 'en-attente';
  }

  /** Rejouer ce qui attend, dans l'ordre. S'arrête au premier échec de réseau. */
  vider(): Promise<void> {
    this.enCours ??= this.viderMaintenant().finally(() => {
      this.enCours = null;
    });
    return this.enCours;
  }

  private async viderMaintenant(): Promise<void> {
    const operations = (await this.lister()).filter((o) => o.refus === undefined);
    for (const op of operations) {
      if (!this.enLigne()) break;
      try {
        await executer(op.geste, this.chantiers(), this.sav());
        await this.stockage.retirer(op.id);
      } catch (erreur) {
        if (estErreurReseau(erreur, this.enLigne())) break;
        await this.stockage.ecrire({
          ...op,
          refus: erreur instanceof Error ? erreur.message : 'Refusé par le serveur.',
        });
      }
      this.prevenir();
    }
    this.prevenir();
  }

  async abandonner(id: string): Promise<void> {
    await this.stockage.retirer(id);
    this.prevenir();
  }
}

// ── Le stockage du téléphone ─────────────────────────────────────────────────

const BASE = 'vla-terrain';
const MAGASIN = 'operations';

function ouvrir(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const demande = indexedDB.open(BASE, 1);
    demande.onupgradeneeded = () => {
      demande.result.createObjectStore(MAGASIN, { keyPath: 'id' });
    };
    demande.onsuccess = () => {
      resolve(demande.result);
    };
    demande.onerror = () => {
      reject(demande.error ?? new Error('Stockage du téléphone indisponible.'));
    };
  });
}

function transaction<T>(
  mode: IDBTransactionMode,
  agir: (magasin: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  return ouvrir().then(
    (base) =>
      new Promise<T>((resolve, reject) => {
        const t = base.transaction(MAGASIN, mode);
        const demande = agir(t.objectStore(MAGASIN));
        t.oncomplete = () => {
          resolve(demande.result);
          base.close();
        };
        t.onerror = () => {
          reject(t.error ?? new Error('Écriture impossible dans le téléphone.'));
          base.close();
        };
      })
  );
}

export const stockageIndexedDb: Stockage = {
  lister: () => transaction('readonly', (m) => m.getAll() as IDBRequest<Operation[]>),
  ecrire: (operation) => transaction('readwrite', (m) => m.put(operation)).then(() => undefined),
  retirer: (id) => transaction('readwrite', (m) => m.delete(id)).then(() => undefined),
};

/** Pour les essais, et pour un navigateur sans IndexedDB (navigation privée de certains téléphones). */
export function stockageMemoire(): Stockage {
  const operations = new Map<string, Operation>();
  return {
    lister: () => Promise.resolve([...operations.values()].map((o) => ({ ...o }))),
    ecrire: (o) => {
      operations.set(o.id, { ...o });
      return Promise.resolve();
    },
    retirer: (id) => {
      operations.delete(id);
      return Promise.resolve();
    },
  };
}
